import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseProvider } from 'src/app/core/services/base-provider';
import { GlobalsService } from 'src/app/core/services/globals.service';
import { LsHelperService } from 'src/app/core/services/ls-helper.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class ProfileService extends BaseProvider {
	showManagement = false;
	itemToEdit: any = {};
	goToTransaction = false;
	private selectedProfileIdSubject = new BehaviorSubject<number | null>(null);
	selectedProfileId$: Observable<number | null> = this.selectedProfileIdSubject.asObservable();
	selectedProfileId: number | null = null;
	openedByAdmin = false;

	// Sistema de caché para perfiles
	private profilesCache: any[] | null = null;
	private cacheTimestamp: number | null = null;
	private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos en milisegundos

	constructor(
		public globals: GlobalsService,
		public http: HttpClient,
		public ls: LsHelperService,
	) {
		super();

		this.baseUrl = 'profile';
		// Initialize with current value
		this.selectedProfileId = this.getSelectedProfileId();
		this.selectedProfileIdSubject.next(this.selectedProfileId);
	}

	getSelectedProfileId() {
		if (this.selectedProfileId) {
			return this.selectedProfileId;
		}

		const profileData = localStorage.getItem('selectedProfile');
		if (profileData && profileData !== 'null') {
			this.selectedProfileId = JSON.parse(profileData).Id;
			this.selectedProfileIdSubject.next(this.selectedProfileId);
		}

		return this.selectedProfileId;
	}

	getSelectedProfile() {
		const profileData = localStorage.getItem('selectedProfile');
		if (profileData) {
			return JSON.parse(profileData);
		}

		return null;
	}

	setSelectedProfileId(id: number | null) {
		this.selectedProfileId = id;
		this.selectedProfileIdSubject.next(id);
	}

	getByCompanyId(id) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/' + id;

			const loginData = this.ls.get('loginData');

			this.http.get(
				this.url
			)
				.subscribe(
					(data: any) => {
						this.itemToEdit = data.Data;

						resolve(this.itemToEdit);
					},
					err => { reject(err); }
				);
		});
	}

	search(params, forceRefresh: boolean = false) {
		return new Promise((resolve, reject) => {
			const hasSearchTerm = params.term && params.term.trim() !== '' && params.term !== 'null';

			if (!forceRefresh && !hasSearchTerm && this.profilesCache && this.cacheTimestamp) {
				const now = Date.now();
				const cacheAge = now - this.cacheTimestamp;

				if (cacheAge < this.CACHE_TTL) {
					this.list = this.profilesCache;
					resolve(this.profilesCache);
					return;
				}
			}

			// Construir URL con endpoint v2
			this.url = this.globals.dataUrl + this.baseUrl + '/v2/user';

			// Construir query params - convertir term a string vacío si es 'null'
			let termValue = params.term || '';
			if (termValue === 'null') {
				termValue = '';
			}

			const queryParams = new HttpParams()
				.set('term', termValue)
				.set('page', params.pageNumber.toString())
				.set('size', params.pageSize.toString());

			this.http
				.get(this.url, { params: queryParams })
				.subscribe(
					(data: any) => {
						this.list = data.Data;

						// Solo cachear si no hay término de búsqueda
						if (!hasSearchTerm) {
							this.profilesCache = data.Data;
							this.cacheTimestamp = Date.now();
						}

						resolve(data.Data);
					},
					err => { reject(err); }
				);
		});
	}

	// Limpia la caché de perfiles
	clearProfilesCache() {
		this.profilesCache = null;
		this.cacheTimestamp = null;
	}

	getById(id) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl +
				'/' + id;

			this.http
				.get(this.url)
				.subscribe(
					(data: any) => {
						resolve(data.Data);
					},
					err => { reject(err); }
				);
		});
	}

	getByUser(params) {
		return new Promise((resolve, reject) => {
			this.url = `${this.globals.dataUrl}${this.baseUrl}/v2/user`;

			const termValue = params.term && params.term.trim() !== '' ? params.term.trim() : 'null';

			const queryParams = new HttpParams()
				.set('term', termValue)
				.set('page', params.pageNumber.toString())
				.set('size', params.pageSize.toString());

			this.http
				.get(this.url, { params: queryParams })
				.subscribe(
					(data: any) => {
						this.list = data.Data || [];
						resolve(data.Data || []);
					},
					err => { reject(err); }
				);
		});
	}


	create(params) {
		return new Promise((resolve, reject) => {
			const url = this.globals.dataUrl + this.baseUrl;

			this.http
				.post(
					url,
					params
				)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	update(params) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/' + params.Id;

			this.http
				.put(this.url, params)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	createV2(params) {
		return new Promise((resolve, reject) => {
			const url = this.globals.dataUrl + this.baseUrl + '/postV2';

			this.http
				.post(
					url,
					params
				)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	updateV2(params) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/updateV2/' + params.Id;

			this.http
				.put(this.url, params)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	updateSignature(profileId: number, signature: string) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/signature/' + profileId;

			this.http
				.put(this.url, { signature })
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	getSignature(profileId: number) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/signature/' + profileId;

			this.http
				.get(this.url)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}


	uploadImage(params, profileId: number) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + '/file/' + profileId;

			this.http
				.post(
					this.url,
					params
				)
				.subscribe(
					(data: any) => {
						resolve(data.Data);
					},
					err => {
						reject(err);
					}
				);
		});
	}

	deleteSignature(profileId: number) {
		return new Promise((resolve, reject) => {
			this.url = `${this.globals.dataUrl}${this.baseUrl}/signature/${profileId}`;

			this.http
				.delete(this.url)
				.subscribe(
					(data: any) => {
						resolve(data);
					},
					err => { reject(err); }
				);
		});
	}

	adminGetProfiles(term: string, isIndividual: boolean, isStampsModule: boolean, profileType: string, pageNumber: number, pageSize: number){
		return new Promise((resolve, reject) => {
			let url = `${this.globals.dataUrl}${this.baseUrl}/term/${term}/isIndividual/${isIndividual}/isStampsModule/${isStampsModule}/profileType/${profileType}/page/${pageNumber}/size/${pageSize}`;

			this.http.get(url).subscribe(
				(data : any) => {resolve(data)}
			),
				(err: any) => { reject(err) }
		})
	}

	setProfileAsExempt(data: any){
		return new Promise((resolve, reject) => {
			let url = `${this.globals.dataUrl}${this.baseUrl}/updateParams`;

			this.http.put(url, data).subscribe(
				(data: any) => { resolve(data) }
			),
				(err: any) => { reject(err) }
		});
	}

	getRecommendationBySSN(ssn: string) {
		return new Promise((resolve, reject) => {
			this.url = this.globals.dataUrl + this.baseUrl + "/recommendation/companion/" + ssn;

			this.http
				.get(this.url)
				.subscribe(
					(data: any) => {
						resolve(data.Data);
					},
					err => { reject(err); }
				);
		});
	}

}
